import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api, fmtPrice, fmtDate } from "@/lib/api";
import { PageTitle, Panel, Label } from "@/pages/admin/AdminLayout";
import { REQ_TYPES } from "@/pages/admin/Requests";

const monthLabel = (m) => new Date(`${m}-15T12:00`).toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
const eur = (c) => fmtPrice(c || 0);
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const deliveryUrl = (sid, lang = "pt") => `${BACKEND}/api/admin/sales/${sid}/delivery.pdf?lang=${lang}`;

function GroupTable({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.revenue_cents));
  return (
    <Panel data-testid={testid}>
      <h3 className="text-lg mb-4">{title}</h3>
      {rows.length === 0 ? <p className="text-sm text-[#A39E93]">Sem movimentos neste mês.</p> : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.18em] text-[#A39E93]"><tr>{["", "Vendas", "Faturação", "Reservas", "Sinais", "Valor sinais"].map((h) => <th key={h} className="text-left font-normal pb-2">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-t border-[#282520]">
                <td className="py-2.5 pr-3 min-w-[140px]">{r.name}<div className="h-1 mt-1.5 bg-[#C8AE6E]/70" style={{ width: `${(r.revenue_cents / max) * 100}%` }} /></td>
                <td className="py-2.5">{r.sales}</td><td className="py-2.5 text-[#E6D5AC]">{eur(r.revenue_cents)}</td>
                <td className="py-2.5">{r.reservations}</td><td className="py-2.5">{r.deposits}</td><td className="py-2.5">{eur(r.deposit_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function List({ title, rows, render, testid }) {
  return (
    <Panel data-testid={testid}>
      <h3 className="text-lg mb-3">{title} <span className="text-sm text-[#A39E93]">({rows.length})</span></h3>
      <div className="divide-y divide-[#282520] text-sm max-h-[320px] overflow-y-auto">{rows.length ? rows.map(render) : <p className="text-[#A39E93] py-2">—</p>}</div>
    </Panel>
  );
}

export default function Report() {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/report", { params: { month: month || undefined } }).then((r) => setD(r.data)); }, [month]);
  if (!d) return null;
  const tot = d.totals;
  return (
    <div data-testid="admin-report">
      <PageTitle title="Relatório mensal" sub={`Vendas, reservas e sinais recebidos · ${monthLabel(d.month)}`}>
        <input type="month" className="afield !w-auto [color-scheme:dark]" value={d.month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" />
        <button className="btn-line !py-2" onClick={() => window.print()} data-testid="report-print">Imprimir</button>
      </PageTitle>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {[["Vendas", tot.sales, "sales"], ["Faturação", eur(tot.revenue_cents), "revenue"], ["Reservas (ativas ou vendidas)", tot.reservations, "reservations"], ["Sinais recebidos", tot.deposits, "deposits"], ["Valor dos sinais", eur(tot.deposit_cents), "deposit-value"]].map(([l, v, k]) => (
          <Panel key={k}><Label>{l}</Label><p className="font-display text-3xl text-[#E6D5AC]" data-testid={`report-total-${k}`}>{v}</p></Panel>
        ))}
      </div>
      {tot.on_request_sales > 0 && <p className="text-xs text-[#A39E93] mb-4">{tot.on_request_sales} venda(s) de peças "sob consulta" não entram na faturação (sem preço registado).</p>}
      <div className="grid xl:grid-cols-2 gap-6 mb-6">
        <GroupTable title="Por marca" rows={d.by_brand} testid="report-by-brand" />
        <GroupTable title="Por responsável" rows={d.by_staff} testid="report-by-staff" />
      </div>
      <Panel className="mb-6" data-testid="report-requests"><Label>Pedidos recebidos no mês</Label>
        <div className="flex flex-wrap gap-4 text-sm mt-2">{Object.entries(REQ_TYPES).map(([k, l]) => <span key={k}>{l}: <span className="text-[#E6D5AC]">{tot.requests[k] || 0}</span></span>)}</div>
      </Panel>
      <div className="grid xl:grid-cols-3 gap-6">
        <List title="Vendas" rows={d.sales} testid="report-sales-list" render={(s) => <div key={s.id} className="py-2 flex items-start justify-between gap-3"><div className="min-w-0"><span className="text-[#A39E93] text-xs">{fmtDate(s.at)}</span> {s.brand} {s.model} · <span className="text-[#E6D5AC]">{s.price_cents ? eur(s.price_cents) : "sob consulta"}</span>{s.serial && <span className="block text-[11px] text-[#A39E93] mt-0.5">SN {s.serial}{s.client_name ? ` · ${s.client_name}` : ""}{s.sale_date ? ` · desde ${s.sale_date}` : ""}</span>}<span className="text-xs text-[#A39E93]">· {s.staff}{s.ref ? ` · ${s.ref}` : ""}</span></div>{s.serial && <a href={deliveryUrl(s.id)} target="_blank" rel="noopener noreferrer" className="btn-line !py-1 !px-2 text-[11px] shrink-0" title="Descarregar documento de entrega e garantia (PDF)" data-testid={`report-delivery-${s.id}`}><Download className="w-3 h-3" /> PDF</a>}</div>} />
        <List title="Reservas" rows={d.reservations} testid="report-reservations-list" render={(r, i) => <div key={i} className="py-2"><span className="text-[#A39E93] text-xs">{fmtDate(r.at)}</span> <Link to={`/admin/pedidos/${r.request_id}`} className="hover:text-[#E6D5AC]">{r.brand} {r.model} · {r.client}</Link> <span className="text-xs text-[#A39E93]">· {r.staff} · {r.status === "active" ? "ativa" : r.status === "sold" ? "vendida" : "libertada"}</span></div>} />
        <List title="Sinais recebidos" rows={d.deposits} testid="report-deposits-list" render={(r, i) => <div key={i} className="py-2"><span className="text-[#A39E93] text-xs">{fmtDate(r.at)}</span> {r.brand} {r.model} · <span className="text-[#E6D5AC]">{eur(r.amount_cents)}</span> <span className="text-xs text-[#A39E93]">· {r.staff}</span></div>} />
      </div>
    </div>
  );
}
